import { useParams, useLocation, Link, useNavigate, useSearchParams } from 'react-router-dom';
import { useState, useEffect, useCallback } from 'react';
import { motion } from 'framer-motion';
import { Heart, ArrowRight, Star, CircleNotch, Funnel, Scales, Check, CaretDown, Sparkle, PencilSimple, ShoppingBag, Share, Copy, EnvelopeSimple } from '@phosphor-icons/react';
import axios from 'axios';
import { useAuth } from '@/context/AuthContext';
import { useCurrency } from '@/context/CurrencyContext';
import ShopPickModal from '@/components/ShopPickModal';
import { trackEmailCapture } from '@/lib/analytics';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const flagEmoji = { AU: '\uD83C\uDDE6\uD83C\uDDFA', UK: '\uD83C\uDDEC\uD83C\uDDE7', US: '\uD83C\uDDFA\uD83C\uDDF8' };

// Refine chip patches — kept module-level so we can apply them from URL state on mount.
const REFINE_PATCHES = {
  'cushion':     { arch_type: 'high' },
  'stability':   { arch_type: 'low' },
  'wider':       { foot_width: 'E' },
  'narrower':    { foot_width: 'B' },
  'budget-low':  { budget: '50-100' },
  'budget-mid':  { budget: '100-150' },
  'budget-high': { budget: '200-300' },
  'mix-brands':  { preferred_brands: [] },
};

function applyRefinements(quiz, chipKeys) {
  let q = { ...quiz };
  for (const k of chipKeys) {
    if (REFINE_PATCHES[k]) q = { ...q, ...REFINE_PATCHES[k] };
  }
  return q;
}

export default function ResultsPage() {
  const { profileId } = useParams();
  const location = useLocation();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const [results, setResults] = useState(location.state?.results?.recommendations || []);
  const [quizEcho, setQuizEcho] = useState(location.state?.results?.quiz || null);
  const [loading, setLoading] = useState(!location.state?.results);
  const [refining, setRefining] = useState(null); // chip key currently refining
  const [activeRefinements, setActiveRefinements] = useState(() => {
    const r = searchParams.get('refine');
    return r ? r.split(',').filter(k => REFINE_PATCHES[k]) : [];
  });
  const [shareCopied, setShareCopied] = useState(false);
  const [wishlistIds, setWishlistIds] = useState(new Set());
  const [compareIds, setCompareIds] = useState([]);
  const [expandedIds, setExpandedIds] = useState(new Set());
  const [shopShoe, setShopShoe] = useState(null);
  const { user } = useAuth();
  const { format } = useCurrency();
  const [regionFilter, setRegionFilter] = useState('ALL');

  const toggleExpand = (e, shoeId) => {
    e.preventDefault();
    e.stopPropagation();
    setExpandedIds(prev => {
      const n = new Set(prev);
      if (n.has(shoeId)) n.delete(shoeId); else n.add(shoeId);
      return n;
    });
  };

  // Fetch/rehydrate profile when arriving without location.state (shared link),
  // and re-apply any refinements from ?refine=... query string.
  useEffect(() => {
    let cancelled = false;

    const loadFresh = async () => {
      setLoading(true);
      try {
        // Step 1: load the stored profile
        const res = await fetch(`${API}/quiz/profile/${profileId}`, {
          credentials: 'include',
          cache: 'no-store',
        });
        if (!res.ok) throw new Error(`HTTP ${res.status}`);
        const data = await res.json();
        if (cancelled) return;

        const chipKeys = (searchParams.get('refine') || '')
          .split(',').filter(k => REFINE_PATCHES[k]);

        // Step 2: if refinements are present, re-submit the patched quiz
        if (chipKeys.length > 0 && data.quiz) {
          const patched = applyRefinements(data.quiz, chipKeys);
          const r2 = await fetch(`${API}/quiz/submit`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            credentials: 'include',
            cache: 'no-store',
            body: JSON.stringify(patched),
          });
          if (r2.ok) {
            const d2 = await r2.json();
            if (cancelled) return;
            setResults(d2.recommendations || []);
            setQuizEcho(data.quiz); // keep original quiz so chips stay correct
          } else {
            setResults(data.recommendations || []);
            setQuizEcho(data.quiz || null);
          }
        } else {
          setResults(data.recommendations || []);
          setQuizEcho(data.quiz || null);
        }
      } catch (err) {
        console.error('Failed to load profile:', err);
      } finally {
        if (!cancelled) setLoading(false);
      }
    };

    if (!location.state?.results) {
      loadFresh();
    } else {
      // Came in via navigation with state — still apply refine chips if URL has them
      const chipKeys = (searchParams.get('refine') || '')
        .split(',').filter(k => REFINE_PATCHES[k]);
      if (chipKeys.length > 0 && location.state.results.quiz) {
        (async () => {
          const patched = applyRefinements(location.state.results.quiz, chipKeys);
          try {
            const r2 = await fetch(`${API}/quiz/submit`, {
              method: 'POST',
              headers: { 'Content-Type': 'application/json' },
              credentials: 'include',
              cache: 'no-store',
              body: JSON.stringify(patched),
            });
            if (r2.ok) {
              const d2 = await r2.json();
              if (!cancelled) setResults(d2.recommendations || []);
            }
          } catch (err) { console.error(err); }
        })();
      }
    }

    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [profileId]);

  useEffect(() => {
    if (user) {
      axios.get(`${API}/wishlist`, { withCredentials: true })
        .then(({ data }) => setWishlistIds(new Set(data.map(s => s.shoe_id))))
        .catch(() => {});
    }
  }, [user]);

  const toggleWishlist = async (e, shoeId) => {
    e.preventDefault();
    e.stopPropagation();
    if (!user) { navigate('/login'); return; }
    try {
      if (wishlistIds.has(shoeId)) {
        await axios.delete(`${API}/wishlist/${shoeId}`, { withCredentials: true });
        setWishlistIds(prev => { const n = new Set(prev); n.delete(shoeId); return n; });
      } else {
        await axios.post(`${API}/wishlist`, { shoe_id: shoeId }, { withCredentials: true });
        setWishlistIds(prev => new Set(prev).add(shoeId));
      }
      window.dispatchEvent(new Event('wishlist-updated'));
    } catch (err) {
      if (err.response?.status === 401) navigate('/login');
    }
  };

  const toggleCompare = (e, shoeId) => {
    e.preventDefault();
    e.stopPropagation();
    if (compareIds.includes(shoeId)) {
      setCompareIds(compareIds.filter(id => id !== shoeId));
    } else if (compareIds.length < 4) {
      setCompareIds([...compareIds, shoeId]);
    }
  };

  const goCompare = () => {
    navigate('/compare', { state: { preSelected: compareIds } });
  };

  // Click any quiz echo chip to jump straight to that step in the quiz —
  // prefills the answers and starts on that step instead of step 0.
  const editStep = (stepKey) => {
    navigate('/quiz', { state: { prefill: quizEcho, jumpToStep: stepKey } });
  };

  // ----------------- Save my picks (email capture) -----------------
  const [emailInput, setEmailInput] = useState(user?.email || '');
  const [emailSending, setEmailSending] = useState(false);
  const [emailSent, setEmailSent] = useState(false);
  const [emailError, setEmailError] = useState('');

  const sendPicksEmail = async () => {
    setEmailError('');
    const trimmed = emailInput.trim();
    if (!trimmed || !trimmed.includes('@') || !trimmed.split('@')[1]?.includes('.')) {
      setEmailError('Please enter a valid email address.');
      return;
    }
    setEmailSending(true);
    try {
      const res = await fetch(`${API}/email/send-picks`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        cache: 'no-store',
        body: JSON.stringify({ profile_id: profileId, email: trimmed }),
      });
      if (!res.ok) {
        const d = await res.json().catch(() => ({}));
        throw new Error(d.detail || 'Could not send email');
      }
      // GA: track successful email capture for funnel + retargeting audiences
      trackEmailCapture({ source: 'send_my_picks', region: profile?.region });
      setEmailSent(true);
    } catch (err) {
      setEmailError(err.message || 'Could not send email. Try again.');
    } finally {
      setEmailSending(false);
    }
  };

  // ----------------- Refine my results -----------------
  // Each chip mutates the original quiz answers and re-submits to /api/quiz/submit
  // for a fresh top-8. Used to re-roll without restarting the 8-step quiz.
  const REFINE_CHIPS = [
    { key: 'cushion', label: 'More cushioning', patch: { arch_type: 'high' },
      hideIf: (q) => (q?.arch_type || '').toLowerCase() === 'high' },
    { key: 'stability', label: 'More stability', patch: { arch_type: 'low' },
      hideIf: (q) => ['low', 'flat'].includes((q?.arch_type || '').toLowerCase()) },
    { key: 'wider', label: 'Wider fit', patch: { foot_width: 'E' },
      hideIf: (q) => ['e', '2e', '3e', '4e'].includes((q?.foot_width || '').toLowerCase()) },
    { key: 'narrower', label: 'Narrower fit', patch: { foot_width: 'B' },
      hideIf: (q) => ['b', '2a'].includes((q?.foot_width || '').toLowerCase()) },
    { key: 'budget-low', label: 'Under $100', patch: { budget: '50-100' },
      hideIf: (q) => q?.budget === '50-100' },
    { key: 'budget-mid', label: '$100 – $150', patch: { budget: '100-150' },
      hideIf: (q) => q?.budget === '100-150' },
    { key: 'budget-high', label: '$150+', patch: { budget: '200-300' },
      hideIf: (q) => q?.budget === '200-300' },
    { key: 'mix-brands', label: 'Mix of brands', patch: { preferred_brands: [] },
      hideIf: (q) => !q?.preferred_brands || q.preferred_brands.length === 0 },
  ];

  const refine = async (chip) => {
    if (!quizEcho || refining) return;
    setRefining(chip.key);

    // Toggle chip in active set
    const nextChips = activeRefinements.includes(chip.key)
      ? activeRefinements.filter(k => k !== chip.key)
      : [...activeRefinements, chip.key];

    const patched = applyRefinements(quizEcho, nextChips);
    try {
      const res = await fetch(`${API}/quiz/submit`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify(patched),
        cache: 'no-store',
      });
      if (!res.ok) throw new Error(`Server ${res.status}`);
      const data = await res.json();
      setResults(data.recommendations || []);
      setActiveRefinements(nextChips);
      // Sync URL — share-link / browser-back support
      const params = new URLSearchParams(searchParams);
      if (nextChips.length) params.set('refine', nextChips.join(','));
      else params.delete('refine');
      setSearchParams(params, { replace: true });
      window.scrollTo({ top: 200, behavior: 'smooth' });
    } catch (err) {
      console.error('Refine failed:', err);
    } finally {
      setRefining(null);
    }
  };

  const shareLink = useCallback(async () => {
    const url = window.location.href;
    try {
      if (navigator.share) {
        await navigator.share({
          title: 'My SoleFinder picks',
          text: 'Check out these shoe matches I found on SoleFinder',
          url,
        });
      } else {
        await navigator.clipboard.writeText(url);
        setShareCopied(true);
        setTimeout(() => setShareCopied(false), 2200);
      }
    } catch (err) {
      // User cancelled share dialog — no-op
      if (err?.name !== 'AbortError') console.error('Share failed:', err);
    }
  }, []);

  const visibleChips = quizEcho ? REFINE_CHIPS.filter(c => !c.hideIf(quizEcho)) : [];

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center" data-testid="results-loading">
        <CircleNotch size={48} className="animate-spin text-[#FF3B30]" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-white" data-testid="results-page">
      <div className="max-w-[1400px] mx-auto px-6 md:px-12 py-10">
        <div className="mb-8 flex items-start justify-between gap-4 flex-wrap">
          <div>
            <p className="text-xs font-bold uppercase tracking-[0.2em] text-[#52525B] mb-2">Your Recommendations</p>
            <h1 className="font-heading font-black text-3xl sm:text-4xl tracking-tighter uppercase" data-testid="results-title">
              {results.length} SHOES MATCHED
            </h1>
            <p className="text-[#52525B] mt-2">Based on your fit analysis. Use the heart to save to your wishlist, or the scales to compare side by side.</p>
          </div>
          <button
            type="button"
            onClick={() => navigate('/quiz', { state: { prefill: quizEcho } })}
            disabled={!quizEcho}
            className="inline-flex items-center gap-2 border-2 border-[#0A0A0A] px-4 py-2.5 text-xs font-bold uppercase tracking-wide text-[#0A0A0A] hover:bg-[#0A0A0A] hover:text-white transition-colors disabled:opacity-40 disabled:cursor-not-allowed"
            data-testid="edit-quiz-btn"
            title={quizEcho ? 'Tweak your answers without starting over' : 'Retake the quiz to enable editing'}
          >
            <PencilSimple size={14} weight="bold" />
            Edit my quiz
          </button>
        </div>

        {/* Quiz echo chips — click to jump back to that step */}
        {quizEcho && (
          <div className="mb-6 flex flex-wrap gap-2 text-[11px]" data-testid="quiz-echo-chips">
            <span className="text-[10px] font-bold uppercase tracking-[0.2em] text-[#A1A1AA] self-center mr-1">Edit ›</span>
            {quizEcho.gender && (
              <button onClick={() => editStep('gender')} title="Change gender"
                className="inline-flex items-center gap-1 bg-[#F4F4F5] text-[#52525B] px-2.5 py-1 font-bold uppercase tracking-wide hover:bg-[#FF3B30] hover:text-white transition-colors cursor-pointer"
                data-testid="echo-gender">
                {quizEcho.gender}
              </button>
            )}
            {quizEcho.activity && (
              <button onClick={() => editStep('activity')} title="Change activity"
                className="inline-flex items-center gap-1 bg-[#F4F4F5] text-[#52525B] px-2.5 py-1 font-bold uppercase tracking-wide hover:bg-[#FF3B30] hover:text-white transition-colors cursor-pointer"
                data-testid="echo-activity">
                {quizEcho.activity}
              </button>
            )}
            {quizEcho.size && (
              <button onClick={() => editStep('size')} title="Change shoe size"
                className="inline-flex items-center gap-1 bg-[#F4F4F5] text-[#52525B] px-2.5 py-1 font-bold uppercase tracking-wide hover:bg-[#FF3B30] hover:text-white transition-colors cursor-pointer"
                data-testid="echo-size">
                US {quizEcho.size}
              </button>
            )}
            {quizEcho.foot_width && (
              <button onClick={() => editStep('foot_width')} title="Change width fitting"
                className="inline-flex items-center gap-1 bg-[#F4F4F5] text-[#52525B] px-2.5 py-1 font-bold uppercase tracking-wide hover:bg-[#FF3B30] hover:text-white transition-colors cursor-pointer"
                data-testid="echo-width">
                Width {quizEcho.foot_width}
              </button>
            )}
            {quizEcho.arch_type && (
              <button onClick={() => editStep('arch_type')} title="Change arch type"
                className="inline-flex items-center gap-1 bg-[#F4F4F5] text-[#52525B] px-2.5 py-1 font-bold uppercase tracking-wide hover:bg-[#FF3B30] hover:text-white transition-colors cursor-pointer"
                data-testid="echo-arch">
                {quizEcho.arch_type} arch
              </button>
            )}
            {(quizEcho.preferred_brands || []).slice(0, 3).map(b => (
              <button key={b} onClick={() => editStep('preferred_brands')} title="Change preferred brands"
                className="inline-flex items-center gap-1 bg-[#FF3B30]/10 text-[#FF3B30] px-2.5 py-1 font-bold uppercase tracking-wide hover:bg-[#FF3B30] hover:text-white transition-colors cursor-pointer">
                {b}
              </button>
            ))}
          </div>
        )}

        {/* Refine my results — quick chips to re-roll without restarting */}
        {visibleChips.length > 0 && (
          <div className="mb-6 border border-[#E4E4E7] bg-[#FAFAFA] p-4" data-testid="refine-bar">
            <div className="flex items-start justify-between gap-3 mb-3">
              <p className="text-[10px] font-bold uppercase tracking-[0.2em] text-[#52525B] flex items-center gap-1.5">
                <Sparkle size={12} weight="fill" className="text-[#FF3B30]" />
                Refine instantly
              </p>
              {(activeRefinements.length > 0 || profileId) && (
                <button
                  onClick={shareLink}
                  className="inline-flex items-center gap-1.5 text-[10px] font-bold uppercase tracking-wide text-[#52525B] hover:text-[#FF3B30] transition-colors"
                  data-testid="share-results-btn">
                  {shareCopied ? <Check size={12} weight="bold" /> : <Share size={12} weight="bold" />}
                  {shareCopied ? 'Link copied' : 'Share these picks'}
                </button>
              )}
            </div>
            <div className="flex flex-wrap gap-2">
              {visibleChips.map(chip => {
                const isLoading = refining === chip.key;
                const isActive = activeRefinements.includes(chip.key);
                return (
                  <button
                    key={chip.key}
                    onClick={() => refine(chip)}
                    disabled={!!refining}
                    data-testid={`refine-${chip.key}`}
                    data-state={isActive ? 'on' : 'off'}
                    className={`inline-flex items-center gap-1.5 border-2 px-3 py-1.5 text-[11px] font-bold uppercase tracking-wide transition-all
                      ${isLoading
                        ? 'border-[#FF3B30] bg-white text-[#FF3B30]'
                        : isActive
                          ? 'border-[#FF3B30] bg-[#FF3B30] text-white hover:bg-[#D62828]'
                          : 'border-[#E4E4E7] bg-white text-[#0A0A0A] hover:border-[#FF3B30] hover:text-[#FF3B30]'}
                      disabled:opacity-50 disabled:cursor-not-allowed`}>
                    {isLoading
                      ? <CircleNotch size={12} className="animate-spin" />
                      : isActive ? <Check size={12} weight="bold" /> : null}
                    {chip.label}
                  </button>
                );
              })}
            </div>
            {activeRefinements.length > 0 && (
              <p className="text-[10px] text-[#A1A1AA] mt-3 normal-case tracking-normal">
                Showing refined picks · click an active chip again to remove it
              </p>
            )}
          </div>
        )}

        {/* Region Filter */}
        <div className="flex gap-2 mb-8 flex-wrap" data-testid="region-filter">
          {['ALL', 'AU', 'UK', 'US'].map(r => (
            <button key={r} onClick={() => setRegionFilter(r)}
              className={`px-4 py-2 text-xs font-bold uppercase tracking-wide border-2 transition-colors ${regionFilter === r ? 'border-[#0A0A0A] bg-[#0A0A0A] text-white' : 'border-[#E4E4E7] hover:border-[#0A0A0A]'}`}
              data-testid={`filter-${r.toLowerCase()}`}>
              {r === 'ALL' ? 'All Regions' : `${flagEmoji[r] || ''} ${r}`}
            </button>
          ))}
        </div>

        {/* Results Grid */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
          {results.map((shoe, i) => {
            const filteredPrices = shoe.store_prices?.filter(p => regionFilter === 'ALL' || p.store_region === regionFilter) || [];
            const lowestPrice = filteredPrices.length > 0 ? filteredPrices.reduce((a, b) => a.price < b.price ? a : b) : null;
            const inWl = wishlistIds.has(shoe.shoe_id);
            const inCmp = compareIds.includes(shoe.shoe_id);

            return (
              <motion.div key={shoe.shoe_id}
                className={`border bg-white transition-all duration-300 group relative ${inCmp ? 'border-[#FF3B30] ring-1 ring-[#FF3B30]/20' : 'border-[#E4E4E7] hover:border-[#0A0A0A]'}`}
                initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: Math.min(i * 0.05, 0.5) }}
                data-testid={`shoe-card-${shoe.shoe_id}`}>
                {/* Match Score Badge */}
                <div className="absolute top-3 left-3 z-10 bg-[#FF3B30] text-white px-3 py-1 text-xs font-bold uppercase" data-testid={`match-score-${shoe.shoe_id}`}>
                  {shoe.match_score}% Match
                </div>

                {/* Action buttons: Wishlist + Compare */}
                <div className="absolute top-3 right-3 z-10 flex flex-col gap-1.5">
                  <button onClick={(e) => toggleWishlist(e, shoe.shoe_id)}
                    className={`w-8 h-8 flex items-center justify-center transition-all ${inWl ? 'bg-[#FF3B30] text-white' : 'bg-white/80 text-[#52525B] hover:text-[#FF3B30]'}`}
                    data-testid={`wishlist-btn-${shoe.shoe_id}`}>
                    <Heart size={16} weight={inWl ? 'fill' : 'regular'} />
                  </button>
                  <button onClick={(e) => toggleCompare(e, shoe.shoe_id)}
                    className={`w-8 h-8 flex items-center justify-center transition-all ${inCmp ? 'bg-[#0A0A0A] text-white' : 'bg-white/80 text-[#52525B] hover:text-[#0A0A0A]'}`}
                    disabled={!inCmp && compareIds.length >= 4}
                    data-testid={`compare-btn-${shoe.shoe_id}`}>
                    <Scales size={16} />
                  </button>
                </div>

                <Link to={`/shoe/${shoe.shoe_id}`}>
                  <div className="overflow-hidden">
                    <img src={shoe.image_url} alt={`${shoe.brand} ${shoe.model}`} className="w-full aspect-square object-cover group-hover:scale-105 transition-transform duration-300" />
                  </div>
                  <div className="px-4 pt-4 pb-3">
                    <p className="text-xs font-bold uppercase tracking-[0.2em] text-[#52525B]">{shoe.brand}</p>
                    <h3 className="font-heading font-bold text-base mt-1" data-testid={`shoe-name-${shoe.shoe_id}`}>{shoe.model}</h3>
                    <p className="text-xs text-[#52525B] mt-1 line-clamp-2">{shoe.match_reason}</p>
                    {lowestPrice && (
                      <div className="mt-3 flex items-center justify-between">
                        <span className="font-bold text-lg" data-testid={`shoe-price-${shoe.shoe_id}`}>
                          {format(lowestPrice.price, lowestPrice.currency)}
                        </span>
                        <span className="text-xs text-[#52525B]">from {lowestPrice.store_name}</span>
                      </div>
                    )}
                    <div className="mt-2 flex items-center justify-between">
                      <div className="flex gap-1 flex-wrap">
                        {[...new Set(shoe.store_prices?.map(p => p.store_region) || [])].map(r => (
                          <span key={r} className="text-xs bg-[#F4F4F5] px-2 py-0.5">{flagEmoji[r] || r}</span>
                        ))}
                      </div>
                      {shoe.available_colors && shoe.available_colors.length > 0 && (
                        <div className="flex gap-1" data-testid={`colors-${shoe.shoe_id}`}>
                          {shoe.available_colors.slice(0, 5).map(c => (
                            <span key={c} className="w-4 h-4 rounded-full border border-[#D4D4D8]" title={c}
                              style={{ backgroundColor: {black:'#0A0A0A',white:'#FFFFFF',grey:'#9CA3AF',brown:'#92400E',blue:'#3B82F6',red:'#EF4444',pink:'#EC4899',green:'#22C55E',navy:'#1E3A5F',nude:'#D4A982',yellow:'#EAB308',orange:'#F97316',purple:'#A855F7'}[c] || '#9CA3AF' }} />
                          ))}
                        </div>
                      )}
                    </div>
                  </div>
                </Link>

                {/* Why this shoe? explainability row */}
                {shoe.match_triggers && shoe.match_triggers.length > 0 && (
                  <div className="border-t border-[#F4F4F5]" data-testid={`why-row-${shoe.shoe_id}`}>
                    <button
                      type="button"
                      onClick={(e) => toggleExpand(e, shoe.shoe_id)}
                      aria-expanded={expandedIds.has(shoe.shoe_id)}
                      className="w-full flex items-center justify-between px-4 py-2.5 text-left hover:bg-[#FAFAFA] transition-colors group/why"
                      data-testid={`why-toggle-${shoe.shoe_id}`}
                    >
                      <span className="inline-flex items-center gap-1.5 text-[11px] font-bold uppercase tracking-wide text-[#0A0A0A]">
                        <Sparkle size={12} weight="duotone" className="text-[#FF3B30]" />
                        Why this shoe?
                        <span className="text-[#A1A1AA] font-medium normal-case tracking-normal">· {shoe.match_triggers.length} reason{shoe.match_triggers.length > 1 ? 's' : ''}</span>
                      </span>
                      <CaretDown
                        size={12}
                        weight="bold"
                        className={`text-[#52525B] transition-transform ${expandedIds.has(shoe.shoe_id) ? 'rotate-180' : ''}`}
                      />
                    </button>
                    {expandedIds.has(shoe.shoe_id) && (
                      <motion.div
                        initial={{ opacity: 0, height: 0 }}
                        animate={{ opacity: 1, height: 'auto' }}
                        className="px-4 pb-4 pt-1 space-y-1.5"
                        data-testid={`why-panel-${shoe.shoe_id}`}
                      >
                        {shoe.match_triggers.map((t, idx) => (
                          <div key={idx} className="flex items-start gap-2 text-[11px] leading-relaxed" data-testid={`why-trigger-${t.kind}-${shoe.shoe_id}`}>
                            <Check size={12} weight="bold" className="text-[#10B981] mt-0.5 flex-shrink-0" />
                            <span>
                              <span className="font-bold text-[#0A0A0A] uppercase tracking-wide">{t.label}</span>
                              <span className="text-[#52525B]"> — {t.detail}</span>
                            </span>
                          </div>
                        ))}
                      </motion.div>
                    )}
                  </div>
                )}

                {/* Shop this pick pill */}
                {shoe.store_prices && shoe.store_prices.length > 0 && (
                  <div className="border-t border-[#F4F4F5] px-4 py-2.5">
                    <button
                      type="button"
                      onClick={(e) => { e.preventDefault(); e.stopPropagation(); setShopShoe(shoe); }}
                      className="w-full inline-flex items-center justify-center gap-1.5 bg-[#0A0A0A] text-white text-[11px] font-bold uppercase tracking-wide py-2.5 hover:bg-[#FF3B30] transition-colors"
                      data-testid={`shop-pick-btn-${shoe.shoe_id}`}
                    >
                      <ShoppingBag size={12} weight="bold" />
                      Shop this pick
                      <span className="opacity-70 font-medium normal-case tracking-normal">
                        · {Math.min(3, shoe.store_prices.filter(p => p.in_stock !== false).length)} retailer{Math.min(3, shoe.store_prices.filter(p => p.in_stock !== false).length) !== 1 ? 's' : ''}
                      </span>
                    </button>
                  </div>
                )}
              </motion.div>
            );
          })}
        </div>

        {results.length === 0 && (
          <div className="text-center py-20">
            <p className="text-[#52525B] text-lg">No recommendations found. Try adjusting your quiz answers.</p>
            <Link to="/quiz" className="inline-flex items-center gap-2 mt-4 bg-[#FF3B30] text-white font-bold uppercase px-8 py-4 hover:bg-[#D62828] transition-colors">
              Retake Quiz <ArrowRight size={20} />
            </Link>
          </div>
        )}

        {/* Save my picks — email capture */}
        {results.length > 0 && (
          <div className="mt-12 border-2 border-[#0A0A0A] bg-[#0A0A0A] text-white" data-testid="save-picks-card">
            {emailSent ? (
              <div className="p-8 text-center" data-testid="save-picks-success">
                <Check size={36} weight="bold" className="text-[#FF3B30] mx-auto mb-3" />
                <h3 className="font-heading font-black text-2xl uppercase tracking-tighter mb-2">Picks sent!</h3>
                <p className="text-[#A1A1AA] text-sm">
                  Check your inbox. We will also let you know when any of these shoes drop in price.
                </p>
              </div>
            ) : (
              <div className="p-6 sm:p-8 grid sm:grid-cols-[1fr_auto] gap-6 items-center">
                <div>
                  <p className="text-[10px] font-bold uppercase tracking-[0.2em] text-[#FF3B30] mb-2 flex items-center gap-1.5">
                    <EnvelopeSimple size={12} weight="fill" /> Save my picks
                  </p>
                  <h3 className="font-heading font-black text-xl sm:text-2xl uppercase tracking-tighter mb-1">
                    Send these picks to your inbox
                  </h3>
                  <p className="text-[#A1A1AA] text-sm">
                    Bookmark these matches in your email and get alerts when they go on sale.
                  </p>
                </div>
                <div className="flex flex-col sm:flex-row gap-3 sm:items-center">
                  <input
                    type="email"
                    inputMode="email"
                    placeholder="you@example.com"
                    value={emailInput}
                    onChange={(e) => { setEmailInput(e.target.value); setEmailError(''); }}
                    onKeyDown={(e) => { if (e.key === 'Enter') sendPicksEmail(); }}
                    disabled={emailSending}
                    className="w-full sm:w-72 px-4 py-3 text-sm bg-white text-[#0A0A0A] border-2 border-white focus:border-[#FF3B30] focus:outline-none disabled:opacity-50"
                    data-testid="save-picks-email-input"
                  />
                  <button
                    onClick={sendPicksEmail}
                    disabled={emailSending || !emailInput.trim()}
                    className="inline-flex items-center justify-center gap-2 bg-[#FF3B30] text-white font-bold uppercase tracking-wide px-6 py-3 text-sm hover:bg-[#D62828] transition-colors disabled:opacity-50 disabled:cursor-not-allowed whitespace-nowrap"
                    data-testid="save-picks-send-btn">
                    {emailSending
                      ? <><CircleNotch size={16} className="animate-spin" /> Sending</>
                      : <>Send picks <ArrowRight size={16} /></>}
                  </button>
                </div>
                {emailError && (
                  <p className="text-xs text-[#FF3B30] sm:col-span-2" data-testid="save-picks-error">{emailError}</p>
                )}
              </div>
            )}
          </div>
        )}
      </div>

      {shopShoe && <ShopPickModal shoe={shopShoe} onClose={() => setShopShoe(null)} />}

      {/* Sticky compare bar */}
      {compareIds.length >= 2 && (
        <motion.div
          initial={{ y: 100 }} animate={{ y: 0 }}
          className="fixed bottom-0 left-0 right-0 bg-[#0A0A0A] text-white py-4 px-6 z-50 flex items-center justify-between"
          data-testid="results-compare-sticky">
          <div className="flex items-center gap-4">
            <Scales size={20} />
            <span className="text-sm font-bold">{compareIds.length} shoes selected</span>
            <div className="hidden sm:flex gap-2">
              {compareIds.map(id => {
                const shoe = results.find(s => s.shoe_id === id);
                return shoe ? (
                  <div key={id} className="flex items-center gap-1 bg-white/10 px-2 py-1 text-xs">
                    <img src={shoe.image_url} alt="" className="w-5 h-5 object-cover" />
                    <span>{shoe.brand}</span>
                    <button onClick={(e) => toggleCompare(e, id)} className="hover:text-[#FF3B30] ml-1">&times;</button>
                  </div>
                ) : null;
              })}
            </div>
          </div>
          <button onClick={goCompare}
            className="bg-[#FF3B30] text-white font-bold uppercase tracking-wide text-sm px-8 py-3 hover:bg-[#D62828] transition-colors flex items-center gap-2"
            data-testid="results-compare-go">
            <Scales size={16} /> Compare Now
          </button>
        </motion.div>
      )}
    </div>
  );
}
